import type { ChangeEvent, ReactNode } from 'react';
import { useState } from 'react';

import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';
import { getIn } from 'formik';
import type { AnyAction, Dispatch } from 'redux';

import { withAPIService } from '../../../networking';
import { withStore } from '../../../store';
import { updateField } from '../../../store/customer';
import { clearFieldError, setErrorFields } from '../../../store/fieldError';
import { ErrorFieldCheck } from '../../ErrorValidation';
import type { ApiServiceLike, FormikLike, StoreLike } from '../types';

interface AddressFieldComponentProps {
  /** Formik object to handle form validation and submission. */
  formik: FormikLike;
  /** Redux store slice containing the customer. */
  store: StoreLike;
  /** Name of the field ex. company.legalName. */
  name: string;
  /** Renders required indicator if true. */
  required?: boolean;
  /** Input type. */
  type?: string;
  /** Label name of the field. */
  label?: string;
  /** Text to render at the bottom of the input. */
  footer?: ReactNode;
  /** Text to render as placeholder of the input. */
  placeholder?: string;
  /** Triggers reducers and updates the store based on action. */
  dispatch: Dispatch<AnyAction>;
  /** Service to make requests to the api (in this case: updateCustomer). */
  apiService: ApiServiceLike;
  /** Disables the input when true. */
  disabled?: boolean;
}

function Component({
  formik,
  store,
  name,
  required,
  type,
  label,
  footer,
  placeholder,
  dispatch,
  apiService,
  disabled
}: AddressFieldComponentProps) {
  const [isDirty, setFieldDirty] = useState(false);
  const [isTouch, setIsTouch] = useState(false);
  // Updates field touched on blur
  // This event will trigger update request to the api when the user focuses out of the input field
  const handleOnBlur = async () => {
    setIsTouch(true);
    if (isDirty) {
      formik.form.setFieldTouched(name, true).then(async error => {
        if (getIn(error, name) === undefined) {
          dispatch(clearFieldError(name));
        } else {
          dispatch(setErrorFields(error));
        }
        if (!hasError) {
          await apiService.updateCustomer(store.customer, dispatch);
          setFieldDirty(false);
        }
      });
    }
  };

  // Get error message if exists
  const errorMessage = getIn(formik.form.errors, name);

  // Boolean to check if field name has error
  const hasError = errorMessage !== undefined;

  // Boolean to check if field is touched
  // const isTouched = getIn(formik.form.touched, name) !== undefined;

  // This event is triggered when the user starts typing on the input fields (for each change event)
  const handleOnChange = (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    // Getting the value from the target input field
    const { value } = event.target;

    setFieldDirty(true);
    // This array handles the anchored properties for addresses thar are needed to update in the customer reducer
    const addressValuesToUpdate: { name: string; value: string }[] = [];

    // The first element of this array is the actual field beign updated
    [{ name, value }, ...addressValuesToUpdate].forEach(item => {
      dispatch(updateField({ name: item.name, value: item.value }));
      formik.form.setFieldValue(item.name, item.value);
    });
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        width: '100%',
        mb: '16px',
        paddingTop: '4px'
      }}
    >
      <TextField
        variant="outlined"
        fullWidth
        disabled={disabled}
        type={type}
        name={name}
        label={label || ''}
        required={!!required}
        slotProps={{
          htmlInput: {
            'aria-required': !!required
          }
        }}
        error={hasError && isTouch}
        onBlur={handleOnBlur}
        value={formik.field?.value || ''}
        placeholder={placeholder}
        onChange={handleOnChange}
      />
      <ErrorFieldCheck hasError={hasError} isTouch={isTouch} errorMessage={errorMessage} setIsTouch={setIsTouch} />
      {footer && (
        <Box
          sx={{
            mt: '3px',
            fontSize: '12px',
            fontWeight: 300,
            '& a': { color: 'var(--cargill-green)' }
          }}
        >
          {footer}
        </Box>
      )}
    </Box>
  );
}

// Injecting APIClient to component from APIContext to use it as props
const connectAPI = withAPIService(Component);

// Injecting store and dispatch to component from StoreConext to use as props
const AddressField = withStore(connectAPI);

export default AddressField;
